import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  AutocompleteGroupSearch,
  AutocompleteSearch,
  Loader,
  Checkbox,
  NumberInputDropdown,
  Dropdown,
} from 'components/UIElements'
import { deepCompareJSON, setObjectProperty } from 'utils/object'
import STRINGS from 'utils/strings'
import { DEPLOYMENT_KEYS, DEPLOYMENT_VALUES } from 'utils/constants'
import { getChainDeploymentStudyScheduleIds } from 'utils/chainDeploymentUtils'
import {
  alignExpiryWithDeployment,
  buildChainExpiryInfo,
  collectExpireVisits,
  getArmIdForVisitScheduleId,
  getArmsFromScheduleIds,
  getArmsMissingExpireVisits,
  getCyclesForArmIds,
  isExpireVisitInStudy,
} from 'utils/chainExpiryUtils'
import _dayOptions from '../../constants/schedulerConstants'

export { isExpireVisitInStudy }

const EXPIRE_BY_VISIT = DEPLOYMENT_VALUES.expireByVisit
const CHAIN_EXPIRY_INFO_KEY = DEPLOYMENT_KEYS.chainExpiryInfo
const EXPIRE_ON_VISIT_CONFIRMATION_KEY = DEPLOYMENT_KEYS.expireOnVisitConfirmation
const EXPIRE_ON_CONFIRM_KEY = DEPLOYMENT_KEYS.expireOnConfirm
const NEVER_KEY = DEPLOYMENT_KEYS.never
const EMPTY_CHAIN_INFO = {}

const getScheduleAndVisitName = ({ visit_id, visit_schedule_id, visitTemplates }) => {
  if (!visitTemplates || !Array.isArray(visitTemplates) || visitTemplates.length === 0) {
    return { _visitScheduleName: '', _visitName: '' }
  }
  const currentVisitSchedule = visitTemplates.reduce((_, currentTemplate) => {
    if (currentTemplate.id === visit_schedule_id) return currentTemplate
    return _
  }, {})
  const _visitScheduleName = currentVisitSchedule?.name || ''
  const currentVisit = currentVisitSchedule?.visits?.reduce((_, nextVisit) => {
    if (nextVisit.id === visit_id) return nextVisit
    return _
  }, {})

  return { _visitScheduleName, _visitName: currentVisit?.name || '' }
}

const InstrumentExpireVisitSelection = props => {
  const {
    chain_deployment_info: chainDeploymentInfoProp,
    chain_expiry_info: chainExpiryInfoProp,
    clearPrevalidatedError,
    disabled,
    errors,
    expire,
    fetchVisitTemplates,
    id, // Unique identifier to differentiate between multiple instances on the same page
    isActive, // Single prop to indicate if this component is active (for both never expire and expire after occurrences)
    recurring, // Recurring configuration to determine if "expire after occurrences" is active
    studyID,
    updateExpire,
    updateExpireField,
    updateMetadataField,
    updateRecurring,
    updateScheduleKey,
    updatePrevalidatedErrors,
    visitTemplates,
    isVisitCycleSupported,
    isArmsSupported,
    studyVisitSchedule = [],
  } = props
  const chain_deployment_info = chainDeploymentInfoProp || EMPTY_CHAIN_INFO
  const chain_expiry_info = chainExpiryInfoProp || EMPTY_CHAIN_INFO

  // Create a unique key for this component instance to prevent conflicts between multiple instances
  const componentKey = id || 'default'

  // Checkbox state:
  // - If backend/config explicitly sets expire_on_visit_confirmation, honor that.
  // - Otherwise, default to false; parent (ExpireContent) will explicitly
  //   enable it by default only for the "never expire" flow.
  const [expireOnVisitConfirmation, setExpireOnVisitConfirmation] = useState(() => {
    const explicit = expire?.[EXPIRE_ON_VISIT_CONFIRMATION_KEY]
    if (typeof explicit === 'boolean') return explicit
    return false
  })
  const prevIsActiveRef = useRef(isActive)
  const prevExpireOnVisitConfirmationRef = useRef(expireOnVisitConfirmation)
  const isUpdatingRef = useRef(false) // Flag to prevent infinite loops
  const [visitTemplatesLoading, setVisitTemplatesLoading] = useState(false)
  const [checkedVisitTemplate, setCheckedVisitTemplate] = useState({})

  // Sync checkbox state when active state changes:
  // - When becoming active, mirror the explicit expire_on_visit_confirmation flag if provided.
  // - Also react when the explicit flag changes while active (e.g. parent sets a default).
  // - When becoming inactive, keep the current local state (UI will be disabled anyway).
  useEffect(() => {
    if (isUpdatingRef.current) return // Prevent updates during sync operations

    const activeChanged = prevIsActiveRef.current !== isActive
    prevIsActiveRef.current = isActive

    if (isActive) {
      const explicit = expire?.[EXPIRE_ON_VISIT_CONFIRMATION_KEY]
      if (typeof explicit === 'boolean' && explicit !== expireOnVisitConfirmation) {
        isUpdatingRef.current = true
        setExpireOnVisitConfirmation(explicit)
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 0)
      }
    }
  }, [isActive, expire?.[EXPIRE_ON_VISIT_CONFIRMATION_KEY], expireOnVisitConfirmation])

  // Update expire_on_visit_confirmation when checkbox state changes and component is active
  // Also remove chain_expiry_info from payload if checkbox is unchecked
  useEffect(() => {
    if (isUpdatingRef.current) return // Prevent updates during sync operations

    // Only update if the value actually changed
    if (prevExpireOnVisitConfirmationRef.current === expireOnVisitConfirmation) return
    prevExpireOnVisitConfirmationRef.current = expireOnVisitConfirmation

    // Inactive instances must not write shared expire flags. Two mounted copies
    // (never-expire vs expire-after-occurrences) racing on this field is what
    // made the visit-confirmation checkbox flicker when a second trigger arm was selected.
    if (!isActive) return

    updateExpireField(EXPIRE_ON_VISIT_CONFIRMATION_KEY, expireOnVisitConfirmation)

    if (!expireOnVisitConfirmation) {
      const hasVisitsSelected =
        chain_expiry_info?.expiry_condition?.terms?.length > 0 ||
        chain_expiry_info?.visit_id ||
        chain_expiry_info?.visits?.length > 0

      if (!hasVisitsSelected) {
        updateScheduleKey(CHAIN_EXPIRY_INFO_KEY, null)
        if (updateRecurring && id === 'never-expire') {
          updateRecurring(null)
        }
      }
    }
  }, [
    expireOnVisitConfirmation,
    isActive,
    chain_expiry_info?.expiry_condition?.terms,
    chain_expiry_info?.visit_id,
    chain_expiry_info?.visits,
    updateExpireField,
    updateScheduleKey,
    updateRecurring,
    expire,
    updateRecurring,
  ])

  // When one instance's checkbox is checked and it's active, uncheck the other instance's checkbox
  // This ensures only one checkbox is checked at a time
  useEffect(() => {
    if (isUpdatingRef.current) return // Prevent updates during sync operations

    if (!isActive) {
      // If this instance is not active, ensure its checkbox is unchecked
      if (expireOnVisitConfirmation) {
        isUpdatingRef.current = true
        setExpireOnVisitConfirmation(false)
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 0)
      }
      return
    }

    // Determine if the other option is active
    const isExpireAfterOccurrencesActive = !!recurring?.recurrences
    const isNeverExpireActive = !!(expire && NEVER_KEY in expire && expire[NEVER_KEY] && !recurring?.recurrences)

    // If this instance's checkbox is checked but the other option is also active, uncheck this one
    // Only do this if the shared field is true (meaning the other instance might have checked it)
    if (expireOnVisitConfirmation && expire?.[EXPIRE_ON_VISIT_CONFIRMATION_KEY]) {
      if (id === 'expire-after-occurrences' && isNeverExpireActive) {
        // "Expire after occurrences" checkbox is checked, but "Never expire" is also active
        // Uncheck this instance's checkbox
        isUpdatingRef.current = true
        setExpireOnVisitConfirmation(false)
        updateExpireField(EXPIRE_ON_VISIT_CONFIRMATION_KEY, false)
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 0)
      } else if (id === 'never-expire' && isExpireAfterOccurrencesActive) {
        // "Never expire" checkbox is checked, but "Expire after occurrences" is also active
        // Uncheck this instance's checkbox
        isUpdatingRef.current = true
        setExpireOnVisitConfirmation(false)
        updateExpireField(EXPIRE_ON_VISIT_CONFIRMATION_KEY, false)
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 0)
      }
    }
  }, [isActive, id, expireOnVisitConfirmation, recurring?.recurrences, expire, updateExpireField])

  // GOLD-12314: expire arms always mirror trigger arms and are not independently editable.
  const deploymentArmIds = useMemo(
    () => getChainDeploymentStudyScheduleIds(chain_deployment_info),
    [chain_deployment_info],
  )

  const deploymentArms = useMemo(() => {
    if (!isArmsSupported) return []
    const arms = getArmsFromScheduleIds(studyVisitSchedule, deploymentArmIds)
    if (arms.length > 0) return arms
    if (studyVisitSchedule?.length === 1) {
      const singleArm = studyVisitSchedule[0]
      return [{ key: singleArm.id, text: singleArm.name }]
    }
    return []
  }, [deploymentArmIds, isArmsSupported, studyVisitSchedule])

  const expireStudyScheduleIds = useMemo(() => {
    if (deploymentArmIds.length > 0) return deploymentArmIds
    const expireArmIds = getChainDeploymentStudyScheduleIds(chain_expiry_info)
    if (expireArmIds.length > 0) return expireArmIds
    if (studyVisitSchedule?.length === 1) return [studyVisitSchedule[0].id]
    return []
  }, [chain_expiry_info, deploymentArmIds, studyVisitSchedule])

  const expireVisits = useMemo(() => collectExpireVisits(chain_expiry_info), [
    chain_expiry_info?.expiry_condition?.terms,
    chain_expiry_info?.visits,
    chain_expiry_info?.visit_id,
    chain_expiry_info?.visit_schedule_id,
    chain_expiry_info?.study_schedule_id,
    chain_expiry_info?.study_schedule_ids,
  ])

  // Remove expire_on_confirm from metadata.schedule.expire if they exist
  // expire_on_confirm should be in metadata.expire_on_confirm instead.
  // Do not set the field to null — the key would still exist and retrigger this effect.
  useEffect(() => {
    if (!updateExpire) return
    if (!Object.prototype.hasOwnProperty.call(expire || {}, EXPIRE_ON_CONFIRM_KEY)) return
    const { [EXPIRE_ON_CONFIRM_KEY]: _removed, ...rest } = expire
    updateExpire(rest)
  }, [expire, updateExpire])

  // Keep metadata.expire_on_confirm in sync with chain_expiry_info:
  // - When visits are selected, set it to true.
  // - When there is no valid visit selection, clear it so it doesn't keep forcing validation.
  useEffect(() => {
    if (!isActive) return

    const { chain_expiry_by_ss_visit } = chain_expiry_info || {}
    // Check both expireVisits (from chain_expiry_info) and checkedVisitTemplate (for immediate updates)
    const hasVisitsInExpireVisits = expireVisits.length > 0
    const hasVisitsInChecked =
      checkedVisitTemplate &&
      typeof checkedVisitTemplate === 'object' &&
      Object.keys(checkedVisitTemplate).some(scheduleId => {
        const scheduleGroup = checkedVisitTemplate[scheduleId]
        return scheduleGroup && typeof scheduleGroup === 'object' && Object.values(scheduleGroup).some(Boolean)
      })
    const hasVisitSelected = hasVisitsInExpireVisits || hasVisitsInChecked
    const hasChainExpiryInfo = chain_expiry_by_ss_visit === true && hasVisitSelected

    const metadataExpireOnConfirm = props.instrument?.metadata?.[EXPIRE_ON_CONFIRM_KEY]
    const scheduleVisitConfirmation = expire?.[EXPIRE_ON_VISIT_CONFIRMATION_KEY]

    if (hasChainExpiryInfo) {
      // When chain_expiry_info is selected (visits are selected), set expire_on_confirm to true
      // This matches the requirement: "expire_on_confirm will be set as true for an instrument if chain_expiry_info is selected"
      // Also set expire_on_visit_confirmation to true to keep checkbox in sync
      if (!expireOnVisitConfirmation) {
        isUpdatingRef.current = true
        setExpireOnVisitConfirmation(true)
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 0)
      }
      if (scheduleVisitConfirmation !== true) {
        updateExpireField(EXPIRE_ON_VISIT_CONFIRMATION_KEY, true)
      }
      if (metadataExpireOnConfirm !== true) {
        updateMetadataField(EXPIRE_ON_CONFIRM_KEY, true)
      }
    } else if (metadataExpireOnConfirm !== false) {
      // If there is no valid chain_expiry_info selection, explicitly clear expire_on_confirm
      // so stale "true" values don't keep triggering validation when the checkbox is off.
      updateMetadataField(EXPIRE_ON_CONFIRM_KEY, false)
    }
  }, [
    expireVisits.length,
    chain_expiry_info?.chain_expiry_by_ss_visit,
    chain_expiry_info?.expiry_condition?.terms,
    checkedVisitTemplate,
    isActive,
    updateExpireField,
    updateMetadataField,
  ])

  // Do not persist expire_type for expire-by-visit; backend should infer from visit info.
  useEffect(() => {
    if (expire?.expire_type === EXPIRE_BY_VISIT) {
      updateExpireField(DEPLOYMENT_KEYS.expireType, null)
    }
  }, [expire?.expire_type, updateExpireField])

  const handleVisitConfirmationToggle = () => {
    // Each component instance has its own handler bound to its own checkbox with a unique id
    // The Checkbox component doesn't pass the event, so we can't verify which checkbox was clicked
    // However, since each instance has its own handler and checkbox with unique id,
    // cross-instance interference is prevented by React's component isolation
    // The unique id on each checkbox (expire-unless-visit-confirmation-${componentKey}) ensures proper isolation

    const newValue = !expireOnVisitConfirmation

    setExpireOnVisitConfirmation(newValue)
    // Only update the shared field if this instance is active
    // This ensures only the active instance controls the shared state
    if (isActive) {
      updateExpireField(EXPIRE_ON_VISIT_CONFIRMATION_KEY, newValue)
    }

    if (!newValue && isActive) {
      updateScheduleKey(CHAIN_EXPIRY_INFO_KEY, null)
      updateMetadataField(EXPIRE_ON_CONFIRM_KEY, false)
      if (updateRecurring && id === 'never-expire') {
        updateRecurring(null)
      }
      setCheckedVisitTemplate({})
    }
  }

  const resolveArmId = useCallback(
    visit =>
      visit?.study_schedule_id ||
      getArmIdForVisitScheduleId(studyVisitSchedule, visit?.visit_schedule_id) ||
      expireStudyScheduleIds[0],
    [expireStudyScheduleIds, studyVisitSchedule],
  )

  const hasVisitCatalogLoaded = useMemo(() => {
    if (isVisitCycleSupported) {
      return Array.isArray(studyVisitSchedule) && studyVisitSchedule.length > 0
    }
    return Array.isArray(visitTemplates) && visitTemplates.length > 0
  }, [isVisitCycleSupported, studyVisitSchedule, visitTemplates])

  const isVisitPresentInStudy = useCallback(
    visit =>
      isExpireVisitInStudy({
        ...visit,
        isVisitCycleSupported,
        studyVisitSchedule,
        visitTemplates,
      }),
    [isVisitCycleSupported, studyVisitSchedule, visitTemplates],
  )

  // Drop uploaded expire visits that do not exist in the current study so they
  // cannot linger as "Unknown Visit" rows in Expiry Offset after remapping.
  useEffect(() => {
    if (!isActive) return
    if (!hasVisitCatalogLoaded) return
    if (!expireVisits.length && !(chain_expiry_info?.visit_ids?.length > 0)) return

    const existingTerms = Array.isArray(chain_expiry_info?.expiry_condition?.terms)
      ? chain_expiry_info.expiry_condition.terms
      : expireVisits.map(({ visit_id, visit_schedule_id, study_schedule_id }) => ({
          visit_id,
          visit_schedule_id,
          study_schedule_id,
        }))

    const validTerms = existingTerms.filter(term =>
      isVisitPresentInStudy({
        visit_id: term.visit_id,
        visit_schedule_id: term.visit_schedule_id,
      }),
    )
    const nextVisitIds = validTerms.map(term => term.visit_id)
    const currentVisitIds = Array.isArray(chain_expiry_info?.visit_ids) ? chain_expiry_info.visit_ids : []

    const hasInvalidVisits =
      expireVisits.some(visit => !isVisitPresentInStudy(visit)) ||
      currentVisitIds.length !== nextVisitIds.length ||
      currentVisitIds.some(id => !nextVisitIds.includes(id))

    if (!hasInvalidVisits) return

    const nextChecked = validTerms.reduce((acc, { visit_id, visit_schedule_id }) => {
      return setObjectProperty(acc, `${visit_schedule_id}.${visit_id}`, true)
    }, {})
    setCheckedVisitTemplate(nextChecked)

    updateScheduleKey(
      CHAIN_EXPIRY_INFO_KEY,
      buildChainExpiryInfo({
        existing: chain_expiry_info,
        armIds: expireStudyScheduleIds,
        visits: validTerms.map(term => ({
          visit_id: term.visit_id,
          visit_schedule_id: term.visit_schedule_id,
          study_schedule_id: term.study_schedule_id || resolveArmId(term),
        })),
        existingTerms: validTerms,
      }),
    )
  }, [
    hasVisitCatalogLoaded,
    expireVisits,
    chain_expiry_info?.visit_ids,
    chain_expiry_info?.expiry_condition?.terms,
    isVisitPresentInStudy,
    expireStudyScheduleIds,
    isActive,
    resolveArmId,
    updateScheduleKey,
  ])

  const visitTemplatesList = (visitTemplates || []).map(({ id: tid, name, visits }) => ({
    field: tid,
    label: name,
    value: (visits || []).map(({ id, name }) => ({
      key: id,
      text: name,
      value: id,
    })),
  }))

  const cycles = useMemo(
    () => (isVisitCycleSupported ? getCyclesForArmIds(studyVisitSchedule, expireStudyScheduleIds) : []),
    [expireStudyScheduleIds, isVisitCycleSupported, studyVisitSchedule],
  )

  const formattedCyclesList = useMemo(() => {
    const armIndex = armId => {
      const index = expireStudyScheduleIds.indexOf(armId)
      return index === -1 ? Number.MAX_SAFE_INTEGER : index
    }

    const formatted = (cycles || []).map(cycle => ({
      field: cycle.id,
      label: isArmsSupported && cycle.armName ? `${cycle.armName}: ${cycle.name}` : cycle.name,
      hideGroupControl: true,
      value: (cycle.visits || []).map(visit => ({
        key: visit.id,
        text: visit.name,
        value: visit.id,
      })),
      armId: cycle.armId,
    }))

    return formatted
      .filter(cycle => cycle.value.length > 0)
      .sort((a, b) => armIndex(a.armId) - armIndex(b.armId))
  }, [cycles, expireStudyScheduleIds, isArmsSupported])

  // Get deployment visit info to exclude from expire visit selection
  const deploymentVisitIds = useMemo(() => {
    if (Array.isArray(chain_deployment_info?.visit_ids) && chain_deployment_info.visit_ids.length > 0) {
      return chain_deployment_info.visit_ids
    }
    if (chain_deployment_info?.visit_id) {
      return [chain_deployment_info.visit_id]
    }
    return []
  }, [chain_deployment_info?.visit_ids, chain_deployment_info?.visit_id])

  const deploymentVisitPairs = useMemo(() => {
    const visitScheduleIds = chain_deployment_info?.visit_schedule_ids || []
    if (deploymentVisitIds.length === 0) return []

    if (chain_deployment_info?.visit_id && chain_deployment_info?.visit_schedule_id) {
      return [{ visit_id: chain_deployment_info.visit_id, visit_schedule_id: chain_deployment_info.visit_schedule_id }]
    }

    return deploymentVisitIds.map((visitId, index) => ({
      visit_id: visitId,
      visit_schedule_id: visitScheduleIds[index] || chain_deployment_info?.visit_schedule_id || '',
    }))
  }, [
    deploymentVisitIds,
    chain_deployment_info?.visit_schedule_ids,
    chain_deployment_info?.visit_id,
    chain_deployment_info?.visit_schedule_id,
  ])

  // Helper function to mark deployment visit and its cycle/group as disabled
  const markDeploymentVisitDisabled = useCallback(
    list => {
      if (!list || deploymentVisitPairs.length === 0) {
        return list
      }
      return list.map(item => {
        const deploymentVisitsInGroup = deploymentVisitPairs.filter(pair => pair.visit_schedule_id === item.field)
        if (deploymentVisitsInGroup.length === 0) {
          return item
        }

        const updatedVisits = item.value.map(visit => ({
          ...visit,
          disabled: deploymentVisitPairs.some(
            pair => pair.visit_id === visit.key && pair.visit_schedule_id === item.field,
          ),
        }))
        const allVisitsDisabled = updatedVisits.length > 0 && updatedVisits.every(visit => visit.disabled)

        return {
          ...item,
          disabled: allVisitsDisabled,
          value: updatedVisits,
        }
      })
    },
    [deploymentVisitPairs],
  )

  // Mark deployment visit as disabled in visit templates list
  const filteredVisitTemplatesList = useMemo(() => markDeploymentVisitDisabled(visitTemplatesList), [
    markDeploymentVisitDisabled,
    visitTemplatesList,
  ])

  // Mark deployment visit as disabled in cycles list
  const filteredFormattedCyclesList = useMemo(() => markDeploymentVisitDisabled(formattedCyclesList), [
    markDeploymentVisitDisabled,
    formattedCyclesList,
  ])

  // Helper: Get whether visits are available based on cycle support
  // Count only non-disabled visits
  const hasVisitsAvailable = useMemo(() => {
    if (isVisitCycleSupported) {
      return filteredFormattedCyclesList?.some(visitTemplate => {
        return visitTemplate?.value?.some(visit => !visit.disabled)
      })
    } else {
      return filteredVisitTemplatesList.some(visitTemplate => {
        return visitTemplate.value.some(visit => !visit.disabled)
      })
    }
  }, [isVisitCycleSupported, filteredFormattedCyclesList, filteredVisitTemplatesList])

  // Consolidated validation for visits availability and visit selection
  useEffect(() => {
    // If "Unless a visit confirmation applies" is NOT checked,
    // visits are not required and should not block scheduling.

    if (!isActive) {
      clearPrevalidatedError('noVisits')
      clearPrevalidatedError('noVisit')
      return
    }
    if (!expireOnVisitConfirmation) {
      clearPrevalidatedError('noVisits')
      clearPrevalidatedError('noVisit')
      return
    } else {
      // Validate visits availability only when checkbox is checked
      hasVisitsAvailable ? clearPrevalidatedError('noVisits') : updatePrevalidatedErrors({ noVisits: '' })

      // Validate visit selection (only if checkbox is checked)
      // Use checkedVisitTemplate for immediate validation feedback
      const hasSelected =
        checkedVisitTemplate &&
        typeof checkedVisitTemplate === 'object' &&
        Object.keys(checkedVisitTemplate).some(scheduleId => {
          const scheduleGroup = checkedVisitTemplate[scheduleId]
          return scheduleGroup && typeof scheduleGroup === 'object' && Object.values(scheduleGroup).some(Boolean)
        })
      const missingArmVisits = getArmsMissingExpireVisits(expireStudyScheduleIds, expireVisits, resolveArmId)
      if (!hasSelected || missingArmVisits.length > 0) {
        updatePrevalidatedErrors({ noVisit: '' })
      } else {
        clearPrevalidatedError('noVisit')
      }
    }
  }, [
    isActive,
    hasVisitsAvailable,
    checkedVisitTemplate,
    expireOnVisitConfirmation,
    expireStudyScheduleIds,
    expireVisits,
    resolveArmId,
    clearPrevalidatedError,
    updatePrevalidatedErrors,
  ])

  // Track if we've already fetched visit templates for the current studyID to prevent repeated API calls
  // Use a unique key based on component id to prevent conflicts between multiple instances
  const fetchedStudyIdRef = useRef(null)

  useEffect(() => {
    // Only fetch if we haven't fetched for this studyID yet AND visitTemplates is empty
    // This prevents repeated calls when component remounts but data already exists
    // Each component instance (identified by id) should fetch independently
    if (fetchedStudyIdRef.current === `${componentKey}-${studyID}` || (visitTemplates && visitTemplates.length > 0)) {
      return
    }

    let visitsNotFetched = true
    fetchedStudyIdRef.current = `${componentKey}-${studyID}`
    setVisitTemplatesLoading(true)
    fetchVisitTemplates(studyID).finally(() => {
      if (visitsNotFetched) setVisitTemplatesLoading(false)
    })
    return () => {
      visitsNotFetched = false
      clearPrevalidatedError('noVisits')
      clearPrevalidatedError('noVisit')
    }
    // Only depend on studyID and componentKey - fetch once per study per component instance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyID, componentKey])

  const isVisitConfirmationEnabled = expireOnVisitConfirmation || expire?.[EXPIRE_ON_VISIT_CONFIRMATION_KEY] === true

  // GOLD-12314: keep expire arms locked to trigger arms and drop visits that no longer apply.
  useEffect(() => {
    if (!isActive || !isVisitConfirmationEnabled) return
    if (isUpdatingRef.current) return
    const hasExistingExpiry =
      !!chain_expiry_info?.expiry_condition ||
      !!chain_expiry_info?.visit_id ||
      !!chain_expiry_info?.visits ||
      chain_expiry_info?.chain_expiry_by_ss_visit === true
    // Do not create chain_expiry_info from scratch here. Writing an empty payload
    // when Triggered by Visit is first selected retriggers sibling effects.
    if (!hasExistingExpiry) return

    const nextExpiry = alignExpiryWithDeployment({
      chainExpiryInfo: chain_expiry_info,
      deployArmIds: expireStudyScheduleIds,
      deployVisitPairs: deploymentVisitPairs,
      resolveArmId,
    })

    if (!nextExpiry || deepCompareJSON(nextExpiry, chain_expiry_info)) return

    updateScheduleKey(CHAIN_EXPIRY_INFO_KEY, nextExpiry)
  }, [
    isActive,
    isVisitConfirmationEnabled,
    chain_expiry_info,
    expireStudyScheduleIds,
    deploymentVisitPairs,
    resolveArmId,
    updateScheduleKey,
  ])

  const getSelectedVisitNames = useCallback(() => {
    const visitsFromChecked = []
    if (checkedVisitTemplate && typeof checkedVisitTemplate === 'object') {
      Object.keys(checkedVisitTemplate).forEach(visitScheduleId => {
        const scheduleGroup = checkedVisitTemplate[visitScheduleId]
        if (scheduleGroup && typeof scheduleGroup === 'object') {
          Object.keys(scheduleGroup).forEach(visitId => {
            if (scheduleGroup[visitId]) {
              visitsFromChecked.push({ visit_id: visitId, visit_schedule_id: visitScheduleId })
            }
          })
        }
      })
    }

    const visitsToDisplay = visitsFromChecked.length > 0 ? visitsFromChecked : expireVisits
    if (visitsToDisplay.length === 0) return []

    return visitsToDisplay
      .map(({ visit_id: visitId, visit_schedule_id: visitScheduleId }) => {
        if (isVisitCycleSupported) {
          const cycle = cycles?.find(item => item.id === visitScheduleId)
          const cycleVisit = cycle?.visits?.find(item => item.id === visitId)
          if (cycle && cycleVisit) {
            const tagText =
              isArmsSupported && cycle.armName
                ? `${cycle.armName}: ${cycle.name}: ${cycleVisit.name}`
                : `${cycle.name}: ${cycleVisit.name}`
            return {
              id: `${visitScheduleId}.${visitId}`,
              key: `${visitScheduleId}.${visitId}`,
              text: tagText,
              visit_id: visitId,
              visit_schedule_id: visitScheduleId,
            }
          }
        } else {
          const { _visitScheduleName, _visitName } = getScheduleAndVisitName({
            visit_id: visitId,
            visit_schedule_id: visitScheduleId,
            visitTemplates,
          })
          if (_visitName) {
            const armId = getArmIdForVisitScheduleId(studyVisitSchedule, visitScheduleId)
            const armName = deploymentArms.find(arm => arm.key === armId)?.text || ''
            return {
              id: `${visitScheduleId}.${visitId}`,
              key: `${visitScheduleId}.${visitId}`,
              text: `${armName ? `${armName}: ` : ''}${_visitScheduleName}: ${_visitName}`,
              visit_id: visitId,
              visit_schedule_id: visitScheduleId,
            }
          }
        }
        return null
      })
      .filter(Boolean)
  }, [
    checkedVisitTemplate,
    cycles,
    deploymentArms,
    expireVisits,
    isArmsSupported,
    isVisitCycleSupported,
    studyVisitSchedule,
    visitTemplates,
  ])

  const selectedVisitTags = useMemo(() => getSelectedVisitNames(), [getSelectedVisitNames])

  useEffect(() => {
    const checked = expireVisits.reduce((acc, { visit_id, visit_schedule_id }) => {
      return setObjectProperty(acc, `${visit_schedule_id}.${visit_id}`, true)
    }, {})

    // If expireVisits is empty but checkedVisitTemplate has items, don't overwrite
    // This means a manual update is in progress and expireVisits hasn't updated yet
    const expireVisitsIsEmpty = expireVisits.length === 0
    const hasCheckedItems = checkedVisitTemplate && Object.keys(checkedVisitTemplate).length > 0
    if (expireVisitsIsEmpty && hasCheckedItems) {
      return
    }

    // Only update if the structure actually changed
    const currentCheckedStr = JSON.stringify(checkedVisitTemplate)
    const newCheckedStr = JSON.stringify(checked)
    if (currentCheckedStr !== newCheckedStr) {
      setCheckedVisitTemplate(checked)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expireVisits])

  const persistExpireVisits = updatedVisits => {
    const visits = updatedVisits.map(visit => ({
      ...visit,
      study_schedule_id: resolveArmId(visit),
    }))
    const newChecked = visits.reduce((acc, { visit_id: visitId, visit_schedule_id: visitScheduleId }) => {
      return setObjectProperty(acc, `${visitScheduleId}.${visitId}`, true)
    }, {})
    setCheckedVisitTemplate(newChecked)
    updateScheduleKey(
      CHAIN_EXPIRY_INFO_KEY,
      buildChainExpiryInfo({
        existing: chain_expiry_info,
        armIds: expireStudyScheduleIds,
        visits,
        existingTerms: chain_expiry_info?.expiry_condition?.terms || [],
      }),
    )
  }

  const getCurrentExpireVisits = () => {
    const currentVisitsFromChecked = []
    if (checkedVisitTemplate && typeof checkedVisitTemplate === 'object') {
      Object.keys(checkedVisitTemplate).forEach(visitScheduleId => {
        const scheduleGroup = checkedVisitTemplate[visitScheduleId]
        if (scheduleGroup && typeof scheduleGroup === 'object') {
          Object.keys(scheduleGroup).forEach(visitId => {
            if (scheduleGroup[visitId] && isVisitPresentInStudy({ visit_id: visitId, visit_schedule_id: visitScheduleId })) {
              currentVisitsFromChecked.push({
                visit_id: visitId,
                visit_schedule_id: visitScheduleId,
                study_schedule_id: getArmIdForVisitScheduleId(studyVisitSchedule, visitScheduleId),
              })
            }
          })
        }
      })
    }

    const allCurrentVisits = expireVisits
      .filter(visit => isVisitPresentInStudy(visit))
      .map(visit => ({
        ...visit,
        study_schedule_id: resolveArmId(visit),
      }))

    currentVisitsFromChecked.forEach(visit => {
      const exists = allCurrentVisits.some(
        ({ visit_id: visitId, visit_schedule_id: visitScheduleId }) =>
          visitId === visit.visit_id && visitScheduleId === visit.visit_schedule_id,
      )
      if (!exists) {
        allCurrentVisits.push({
          ...visit,
          study_schedule_id: resolveArmId(visit),
        })
      }
    })

    return allCurrentVisits
  }

  const clearToggle = () => {
    persistExpireVisits([])
  }

  const onToggleVisit = item => {
    if (!item || item.disabled) return

    const isCurrentlyChecked = !!checkedVisitTemplate?.[item.field]?.[item.key]
    const allCurrentVisits = getCurrentExpireVisits()

    let updatedVisits
    if (isCurrentlyChecked) {
      updatedVisits = allCurrentVisits.filter(
        ({ visit_id: visitId, visit_schedule_id: visitScheduleId }) =>
          !(visitId === item.key && visitScheduleId === item.field),
      )
    } else {
      const exists = allCurrentVisits.some(
        ({ visit_id: visitId, visit_schedule_id: visitScheduleId }) =>
          visitId === item.key && visitScheduleId === item.field,
      )
      updatedVisits = exists
        ? allCurrentVisits
        : [
            ...allCurrentVisits,
            {
              visit_id: item.key,
              visit_schedule_id: item.field,
              study_schedule_id: getArmIdForVisitScheduleId(studyVisitSchedule, item.field),
            },
          ]
    }

    persistExpireVisits(updatedVisits)
  }

  const onRemoveVisitTag = tag => {
    const { visit_id: visitId, visit_schedule_id: visitScheduleId } = tag
    persistExpireVisits(
      getCurrentExpireVisits().filter(
        ({ visit_id: currentVisitId, visit_schedule_id: currentScheduleId }) =>
          !(currentVisitId === visitId && currentScheduleId === visitScheduleId),
      ),
    )
  }

  // Check if any visits are selected (from checkedVisitTemplate for immediate feedback)
  const hasSelectedVisits = useMemo(() => {
    if (expireVisits.length > 0) return true
    if (!checkedVisitTemplate || typeof checkedVisitTemplate !== 'object') return false
    return Object.keys(checkedVisitTemplate).some(scheduleId => {
      const scheduleGroup = checkedVisitTemplate[scheduleId]
      return scheduleGroup && typeof scheduleGroup === 'object' && Object.values(scheduleGroup).some(Boolean)
    })
  }, [checkedVisitTemplate, expireVisits.length])

  const missingArmVisits = useMemo(
    () => getArmsMissingExpireVisits(expireStudyScheduleIds, expireVisits, resolveArmId),
    [expireStudyScheduleIds, expireVisits, resolveArmId],
  )

  const getErrorText = () => {
    if (!expireOnVisitConfirmation) return ''

    if (isArmsSupported && expireStudyScheduleIds.length === 0) {
      return STRINGS.selectArmFirst
    }

    if (!hasVisitsAvailable) {
      return STRINGS.errPleaseASelectDiffDeployNoVisits
    }

    if (!hasSelectedVisits) {
      return STRINGS.pleaseSelDifferentVisit
    }

    if (missingArmVisits.length > 0) {
      return STRINGS.pleaseSelVisitFromEachArm
    }

    return errors['schedule.chain_expiry_info.visit_ids']
  }

  const errorText = getErrorText()

  // Only require visit selection if "Unless visit confirmation applies" checkbox is checked
  const hasError =
    expireOnVisitConfirmation &&
    ((!hasSelectedVisits && !!errorText) || missingArmVisits.length > 0 || !hasVisitsAvailable)

  const getList = () => (isVisitCycleSupported ? filteredFormattedCyclesList : filteredVisitTemplatesList)

  const checkIsDisabled = () => {
    if (!expireOnVisitConfirmation) return true
    if (isArmsSupported && expireStudyScheduleIds.length === 0) return true
    return disabled || !hasVisitsAvailable
  }

  const updateExpireTerms = newTerms => {
    const { expiry_condition } = chain_expiry_info

    updateScheduleKey('chain_expiry_info', {
      ...chain_expiry_info,
      expiry_condition: {
        ...expiry_condition, // Preserve other properties of expiry_condition
        operator: 'OR',
        terms: newTerms,
      },
    })
  }

  const handleExpiryOffsetCheckmark = visitID => {
    const { expiry_condition } = chain_expiry_info
    const { terms } = expiry_condition

    const newTerms = terms.map(term => {
      if (term.visit_id === visitID) {
        if (term.days_offset !== undefined) {
          // Remove both days_offset and timeRelated when unchecking
          const { days_offset, timeRelated, ...rest } = term
          return rest
        } else {
          // Add days_offset with default 0 when checking
          return { ...term, days_offset: 0 }
        }
      }
      return term
    })

    updateExpireTerms(newTerms)
  }

  const updateVisitExpireTerms = (visitID, field, val) => {
    const { expiry_condition } = chain_expiry_info
    const { terms } = expiry_condition

    const newTerms = terms.map(term => {
      if (term.visit_id === visitID) {
        return { ...term, [field]: val }
      }
      return term
    })

    updateExpireTerms(newTerms)
  }

  if (visitTemplatesLoading) return <Loader inContainer size={25} />

  return (
    <div>
      {/* Checkbox for "Unless a visit confirmation applies" */}
      <Checkbox
        id={`expire-unless-visit-confirmation-${componentKey}`}
        checked={expireOnVisitConfirmation}
        onClick={handleVisitConfirmationToggle}
        disabled={disabled}
        label={STRINGS.unlessVisitConfirmationApplies}
      />

      <div className='trigger-inst-box border-radius eight' style={{ marginTop: '16px' }}>
        <p>
          <span className={`${disabled ? 'disabled' : ''}`}>{STRINGS.whichVisitExpire}</span>
        </p>
        <div className='arms-visits-selection' style={{ marginTop: '8px' }}>
          {isArmsSupported ? (
            <div className='arms-visits-dropdown with-margin'>
              <div className='arm-name-label-container'>
                <div className='label-small'>{STRINGS.armName}</div>
                {deploymentArms.length > 0 && (
                  <div className='arm-selection-count'>
                    <span className='arm-selection-count-number'>{deploymentArms.length}</span>
                    {` arm${deploymentArms.length === 1 ? '' : 's'} selected`}
                  </div>
                )}
              </div>
              {deploymentArms.length > 0 && (
                <div className='visit-tags-container'>
                  {deploymentArms.map(arm => (
                    <div key={arm.key} className='flexed center-aligned visit-tag'>
                      <span className='visit-tag-text'>{arm.text}</span>
                    </div>
                  ))}
                </div>
              )}
              <AutocompleteSearch
                loading={false}
                disabled
                checked={{}}
                toggleItem={() => {}}
                id={`instrument-expire-arm-${componentKey}`}
                placeholder={STRINGS.searchForArmPlaceholder}
                list={[]}
              />
            </div>
          ) : null}

          <div className='arms-visits-dropdown'>
            <div className='visit-name-label-container'>
              <div className='label-small'>{STRINGS.visitName}</div>
              {selectedVisitTags.length > 0 && (
                <div className='visits-assigned-label'>
                  <span className='visits-assigned-count-number'>{selectedVisitTags.length}</span>
                  {` visit${selectedVisitTags.length === 1 ? '' : 's'} assigned`}
                </div>
              )}
            </div>
            {selectedVisitTags.length > 0 && (
              <div className='visit-tags-container'>
                {selectedVisitTags.map(tag => (
                  <div key={tag.id} className='flexed center-aligned visit-tag'>
                    <span className='visit-tag-text'>{tag.text}</span>
                    {!disabled && expireOnVisitConfirmation && (
                      <button
                        type='button'
                        className='visit-tag-remove'
                        onClick={() => onRemoveVisitTag(tag)}
                        aria-label={`Remove ${tag.text}`}>
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <AutocompleteGroupSearch
              hasError={hasError}
              errorText={errorText}
              absolutePositionedList
              filterWithoutGroupSelected
              groupComponent='label'
              onBackspacePress={clearToggle}
              list={getList() || []}
              onSearchEmpty
              toggleItem={onToggleVisit}
              checked={checkedVisitTemplate}
              placeholder={STRINGS.searchForVisitPlaceholder}
              value=''
              id={`instrument-expire-visit-${componentKey}`}
              disabled={checkIsDisabled()}
            />
          </div>
        </div>
        {selectedVisitTags.length > 0 && (
          <div className='offset-dinamic-expirity'>
            <p>{STRINGS.expireOffset}</p>
            {selectedVisitTags.map(tag => {
              const visitID = tag.visit_id
              const visitName = tag.text.includes(':')
                ? tag.text
                    .split(':')
                    .pop()
                    .trim()
                : tag.text
              const currentTerm = chain_expiry_info?.expiry_condition?.terms?.find(term => term.visit_id === visitID)
              return (
                <div key={tag.id} className='offset-item'>
                  <Checkbox
                    id={`expire-offset-${visitID}`}
                    checked={currentTerm?.days_offset !== undefined}
                    onClick={() => handleExpiryOffsetCheckmark(visitID)}
                    disabled={disabled}
                    label={STRINGS.expire}
                  />
                  <NumberInputDropdown
                    id='offset-days-input'
                    disabled={
                      disabled || currentTerm?.days_offset === undefined || currentTerm?.timeRelated === 'on_the_day'
                    }
                    value={currentTerm?.days_offset !== undefined ? Math.abs(currentTerm?.days_offset) : ''}
                    options={_dayOptions}
                    onChange={val => updateVisitExpireTerms(visitID, 'days_offset', val)}
                    onSelect={val => updateVisitExpireTerms(visitID, 'days_offset', val)}
                    min={0}
                    max={30}
                  />
                  <Dropdown
                    disabled={
                      disabled || currentTerm?.days_offset === undefined || currentTerm?.timeRelated === 'on_the_day'
                    }
                    id='time-unit-options-dropdown'
                    selected={'days'}
                    onSelect={() => {}}
                    options={[
                      {
                        key: 'days',
                        text: 'days',
                      },
                    ]}
                  />
                  <Dropdown
                    disabled={disabled || currentTerm?.days_offset === undefined}
                    id='prior-options-dropdown'
                    selected={currentTerm?.timeRelated}
                    onSelect={option => updateVisitExpireTerms(visitID, 'timeRelated', option.key)}
                    options={[
                      {
                        key: 'before',
                        text: 'before',
                      },
                      {
                        key: 'after',
                        text: 'after',
                      },
                      {
                        key: 'on_the_day',
                        text: 'On the day',
                      },
                    ]}
                  />
                  <span>the {visitName} visit</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

InstrumentExpireVisitSelection.propTypes = {
  chain_deployment_info: PropTypes.shape({
    visit_id: PropTypes.string,
    visit_schedule_id: PropTypes.string,
    visit_ids: PropTypes.arrayOf(PropTypes.string),
    visit_schedule_ids: PropTypes.arrayOf(PropTypes.string),
    study_schedule_id: PropTypes.string,
    study_schedule_ids: PropTypes.arrayOf(PropTypes.string),
  }),
  chain_expiry_info: PropTypes.shape({
    chain_expiry_by_ss_visit: PropTypes.bool,
    visit_id: PropTypes.string,
    visit_schedule_id: PropTypes.string,
    visit_ids: PropTypes.arrayOf(PropTypes.string),
    visits: PropTypes.arrayOf(
      PropTypes.shape({
        visit_id: PropTypes.string,
        visit_schedule_id: PropTypes.string,
      }),
    ),
    study_schedule_id: PropTypes.string,
    study_schedule_ids: PropTypes.arrayOf(PropTypes.string),
  }),
  clearPrevalidatedError: PropTypes.func,
  disabled: PropTypes.bool,
  errors: PropTypes.objectOf(PropTypes.string),
  expire: PropTypes.shape({
    expire_on_visit_confirmation: PropTypes.bool,
  }),
  fetchVisitTemplates: PropTypes.func,
  id: PropTypes.string, // Unique identifier to differentiate between multiple instances on the same page
  isActive: PropTypes.bool, // Indicates if component is active (for both never expire and expire after occurrences)
  recurring: PropTypes.object, // Recurring configuration to determine if "expire after occurrences" is active
  studyID: PropTypes.number,
  updateExpire: PropTypes.func,
  updateExpireField: PropTypes.func,
  updateMetadataField: PropTypes.func,
  updateRecurring: PropTypes.func,
  updateScheduleKey: PropTypes.func,
  updatePrevalidatedErrors: PropTypes.func,
  visitTemplates: PropTypes.arrayOf(PropTypes.object),
  isVisitCycleSupported: PropTypes.bool,
  isArmsSupported: PropTypes.bool,
  studyVisitSchedule: PropTypes.arrayOf(PropTypes.object),
}

export default InstrumentExpireVisitSelection
